import { CARRIER_REQUEST_STATUS } from './constants'
import type { CarrierRequestStatus } from './constants'
import type { CarrierRequestItem } from './types'

export type StatusBannerVariant =
  | 'carrierPlacedBid'
  | 'offerSent'
  | 'counterOfferSent'
  | 'carrierAccepted'
  | 'autoDeclined'
  | 'carrierCanceled'
  | 'offerCanceled'

export interface StatusBanner {
  variant: StatusBannerVariant
  amount?: number
  at?: string
}

type BannerBuilder = (request: CarrierRequestItem) => StatusBanner

const BANNER_BY_STATUS: Partial<Record<CarrierRequestStatus, BannerBuilder>> = {
  // A broker-initiated `send_offer` carries no counter price — the offer is the
  // amount the carrier asked for, so fall back to the requested price.
  [CARRIER_REQUEST_STATUS.offer_sent]: (r) => ({
    variant: 'offerSent',
    amount: r.brokerCounterPrice ?? r.price?.amount,
    // `send_offer` stamps `accepted_at` rather than a counter-offer date.
    at: r.counterOfferSentAt ?? r.respondedAt,
  }),
  [CARRIER_REQUEST_STATUS.counter_sent]: (r) => ({
    variant: 'counterOfferSent',
    amount: r.brokerCounterPrice ?? r.price?.amount,
    at: r.counterOfferSentAt,
  }),
  [CARRIER_REQUEST_STATUS.accepted]: (r) => ({
    variant: 'carrierAccepted',
    amount: r.brokerCounterPrice ?? r.price?.amount,
    at: r.respondedAt,
  }),
  [CARRIER_REQUEST_STATUS.declined]: (r) => ({
    variant: 'autoDeclined',
    at: r.respondedAt,
  }),
  [CARRIER_REQUEST_STATUS.withdrawn]: (r) => ({
    variant: 'carrierCanceled',
    at: r.respondedAt,
  }),
  [CARRIER_REQUEST_STATUS.canceled]: (r) => ({
    variant: 'offerCanceled',
    at: r.respondedAt,
  }),
}

/**
 * Derives the status banner shown at the top of a carrier-request row.
 * Returns `null` for a fresh, untouched request (no banner — e.g. a plain
 * pending bid with no prior counter offer).
 */
export function deriveStatusBanner(
  request: CarrierRequestItem,
): StatusBanner | null {
  // A pending bid that follows a counter offer is the carrier re-bidding.
  if (request.status === CARRIER_REQUEST_STATUS.pending) {
    return request.counterOfferSentAt != null
      ? { variant: 'carrierPlacedBid', at: request.counterOfferSentAt }
      : null
  }
  return BANNER_BY_STATUS[request.status]?.(request) ?? null
}
